import { zodResolver } from "@hookform/resolvers/zod";
import { useUpdateBillable } from "@/modules/work/billables/details/update/mutation";
import { useCreateBillable } from "@/modules/work/billables/details/create/mutation";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { billableSchema } from "./schema";
import { getRequiredFields } from "components/form/utils";
import { useEffect, useMemo, useState } from "react";
import { fetchBillableDataForForm } from "./read";

export const useBillableForm = ({ defaultJobId, defaultJobType }) => {
  const createBillableMutation = useCreateBillable();
  const updateBillableMutation = useUpdateBillable();
  const { billableId } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("contracted");

  const schema = useMemo(
    () => billableSchema(defaultJobType, status),
    [defaultJobType, status]
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      job: defaultJobId,
      type: defaultJobType,
      status,
      name: "",
      attributed_to: null,
      amount: undefined,
      invoice_number: undefined,
    },
  });

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "status" && value?.status !== status)
        setStatus(value.status);
    });
    return () => subscription.unsubscribe();
  }, [form, status]);

  const requiredFields = useMemo(() => getRequiredFields(schema), [schema]);

  async function handleCreate(context) {
    const values = context.values;
    const response = await createBillableMutation.mutateAsync({ values });

    navigate(`/work/billables/${response.id}`);
  }

  async function handleArchive() {
    const dirtyValues = {
      status: "archived",
    };
    await handleUpdate(billableId, { dirtyValues });

    form.setValue("status", "archived", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }
  async function handleCancel() {
    const dirtyValues = {
      status: "cancelled",
    };
    await handleUpdate(billableId, { dirtyValues });

    form.setValue("status", "cancelled", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }

  async function handleRead(id) {
    const response = await fetchBillableDataForForm({ id });

    return {
      ...response,
      note: response.note || "",
      amount: response?.amount ? Number(response.amount) : null,
      adjusted_amount: response?.adjusted_amount
        ? Number(response.adjusted_amount)
        : null,
      fixed_billing_config: response.fixed_billing_config || {},
      hourly_billing_config: response.hourly_billing_config || {},
      dedicated_billing_config: response.dedicated_billing_config || {},
    };
  }

  async function handleUpdate(id, context) {
    const values = { ...(context.dirtyValues || {}) };
    if (values?.billing_config) {
      delete values.billing_config;
    }

    await updateBillableMutation.mutateAsync({
      billableId: id,
      values,
    });

    const result = await handleRead(id);
    navigate(-1);
    return result;
  }

  return {
    form,
    requiredFields,
    handleCreate,
    handleRead,
    handleUpdate,
    handleArchive,
    handleCancel,
    jobType: defaultJobType,
  };
};
